import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, HostBinding, HostListener, inject, OnDestroy, OnInit, untracked, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Observable, Subscription } from 'rxjs';
import { HxAuthService, HxError, HxFioPipe, HxUserService, UserBasicModel } from 'hx-services';
import { CallStateService } from '@callcenter-app/service/call-state.service';
import { isOldWorkplaceUrl, WORKPLACE_READY } from '@callcenter-app/service/workplace.config';
import { ShellUiService } from '../shell-ui.service';
import { ThemeService } from '../theme.service';
import { SHELL_ICON } from '../shell-icons';

export type NumberDialogPhase = 'list' | 'setting' | 'reload' | 'error';
export type NumberKind = 'free' | 'busy' | 'mine';

export interface NumberCell {
  id: number;
  kind: NumberKind;
  /** The holder of a busy number, the operator himself on «ваш». */
  who: string;
  initials: string;
}

/**
 * The window «Выберите номер оператора» of the new shell (the canvas, «Шапка — состояния», 4–5). The rules are those of the old header: the numbers of the operator's groups, the busy ones (with the holder) cannot be taken,
 * the operator's own is «ваш»; the shell of stage 4 takes a new number at once, the old workplace (and the build without the shell) reloads the page as it always did. A failure keeps the window open with «Повторить».
 */
@Component({
  selector: 'app-shell-number-dialog',
  templateUrl: './shell-number-dialog.component.html',
  styleUrls: ['./shell-number-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, TranslocoPipe, HxFioPipe, CdkTrapFocus],
})
export class ShellNumberDialogComponent implements OnInit, OnDestroy {
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;
  /** The window belongs to the shell: always the operator's theme, also over an old (light) page. */
  @HostBinding('attr.data-theme') themeAttr = 'light';

  readonly icon = SHELL_ICON;
  readonly ui = inject(ShellUiService);
  open = false;
  phase: NumberDialogPhase = 'list';
  loading = false;
  cells: NumberCell[] = [];
  freeCount = 0;
  busyCount = 0;
  settingId?: number;
  errorText = '';

  private readonly auth = inject(HxAuthService);
  private readonly users = inject(HxUserService);
  private readonly router = inject(Router);
  private readonly tr = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly theme = inject(ThemeService);
  private readonly callState = inject(CallStateService, { optional: true });
  private ids?: number[];
  private holders: UserBasicModel[] = [];
  private slot = '';
  private subs = new Subscription();
  private returnFocus?: HTMLElement | null;

  constructor() {
    effect(() => {
      const open = this.ui.numberDialog();
      untracked(() => open ? this.show() : this.hide());
    });
    effect(() => {
      this.themeAttr = this.theme.theme();
      untracked(() => this.cdr.markForCheck());
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.open) {
      return;
    }
    // the Escape is this window's: the drawer of «Архив заказов» skips a prevented one
    event.preventDefault();
    if (this.phase !== 'setting') {
      this.close();
    }
  }

  ngOnInit(): void {
    this.slot = this.auth.user.asteriskId ?? '';
    if (this.callState && WORKPLACE_READY) {
      this.subs.add(this.callState.state$.subscribe(state => {
        this.slot = state.slot ?? '';
        this.build();
      }));
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  close(): void {
    this.ui.closeNumberDialog();
  }

  pick(cell: NumberCell): void {
    if (cell.kind !== 'free' || this.phase === 'setting') {
      return;
    }
    this.set(cell.id);
  }

  retry(): void {
    if (this.settingId !== undefined) {
      this.set(this.settingId);
    }
  }

  private show(): void {
    this.open = true;
    this.phase = 'list';
    this.errorText = '';
    this.returnFocus = document.activeElement as HTMLElement | null;
    this.load();
    this.cdr.markForCheck();
    setTimeout(() => this.panel?.nativeElement.focus());
  }

  private hide(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.cdr.markForCheck();
    this.returnFocus?.focus?.();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      this.ids = await this.ui.operatorIds();
    } catch {
      this.ids = undefined;
    }
    this.users.getOperatorIdList().subscribe({
      next: list => {
        this.holders = list;
        this.loading = false;
        this.build();
      },
      error: () => {
        this.loading = false;
        this.build();
      },
    });
  }

  private build(): void {
    const mine = +this.slot;
    this.cells = (this.ids ?? []).map(id => {
      const holder = this.holders.find(u => u.asteriskId === `${id}`);
      const kind: NumberKind = id === mine ? 'mine' : holder ? 'busy' : 'free';
      const who = kind === 'mine' ? (this.auth.user.fullname ?? holder?.fullname ?? '') : holder?.fullname ?? '';
      return { id, kind, who, initials: initialsOf(who) };
    });
    this.freeCount = this.cells.filter(c => c.kind === 'free').length;
    this.busyCount = this.cells.filter(c => c.kind === 'busy').length;
    this.cdr.markForCheck();
  }

  private set(id: number): void {
    this.settingId = id;
    this.phase = 'setting';
    this.errorText = '';
    this.cdr.markForCheck();
    // the old workplace learns of the number only at its start: there the page is reloaded as it always was; anywhere else the shell takes the number at once
    const shell = this.callState && WORKPLACE_READY && !isOldWorkplaceUrl(this.router.url) ? this.callState : undefined;
    const done: Observable<unknown> = shell ? shell.changeSlot(id) : this.users.setOperatorId(id);
    done.subscribe({
      next: () => {
        if (shell) {
          this.phase = 'list';
          this.close();
        } else {
          this.phase = 'reload';
          this.cdr.markForCheck();
          window.location.reload();
        }
      },
      error: (err: HttpErrorResponse) => {
        // the number is taken or not allowed for the operator's groups — the list is asked again, it may have changed
        const ex = err.error as Partial<HxError> | null;
        this.phase = 'error';
        this.errorText = ex?.message ? this.tr.translate(ex.message, ex.data ?? {}) : '';
        this.load();
      },
    });
  }
}

function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
}
